/**
 * Atmosphere presets — the registry + level-id routing for the per-scene "alive"
 * ambiance (the djmsqrvve vibe as data). A preset is an ordered list of
 * EffectLayer (the composable unit); the AtmosphereLayer builds each layer via
 * the EFFECT_REGISTRY. The preset DEFINITIONS live one-per-file under
 * `presets/` (no monolithic config); this file holds the shared spec types, the
 * registry, and `presetForLevel`.
 *
 * The FireflySpec / GodRaySpec / GlowBlobSpec shapes below are the layer payloads
 * for those three kinds (the per-preset files reference them via EffectLayer).
 */
import type { EffectLayer } from './effectLayer.types';
import { COZY_FOREST } from './presets/cozyForest';
import { SUBTLE } from './presets/subtle';
import { DUNGEON } from './presets/dungeon';
import { CAVE } from './presets/cave';
import { VOLCANIC } from './presets/volcanic';
import { ARCTIC } from './presets/arctic';
import { UNDERWATER } from './presets/underwater';
import { DESERT } from './presets/desert';
import { CORRUPTED } from './presets/corrupted';
import { SWAMP } from './presets/swamp';
import { FUNGAL } from './presets/fungal';
import { JUNGLE } from './presets/jungle';
import { MEADOW } from './presets/meadow';
import { CELESTIAL } from './presets/celestial';
import { AUTUMN } from './presets/autumn';
import { ASHLANDS } from './presets/ashlands';
import { TEMPEST } from './presets/tempest';
import { COAST } from './presets/coast';
import { RADIANT } from './presets/radiant';
import { MOOR } from './presets/moor';
import { SAVANNA } from './presets/savanna';
import { SKYLANDS } from './presets/skylands';
import { BAMBOO } from './presets/bamboo';
import { ABYSS } from './presets/abyss';
import { TWILIGHT } from './presets/twilight';
import { FROSTSPIRE } from './presets/frostspire';
import { CRIMSON } from './presets/crimson';
import { PETRIFIED } from './presets/petrified';
import { SAKURA } from './presets/sakura';
import { NEXUS } from './presets/nexus';
import { ARENA } from './presets/arena';
import { SUNKEN_DRIFT } from './presets/sunkenDrift';
import { SKY_WHALE } from './presets/skyWhale';
import { VOID_SERPENT } from './presets/voidSerpent';
import { FOSSIL_GRAVEYARD } from './presets/fossilGraveyard';
// The Hollow Coil cozy→spooky starter island — four bespoke biomes (the
// emotional gradient: bright beach → amber village → cold moor → dark undercroft).
import { PIG_BEACH } from './presets/pigBeach';
import { TIDEWEND, TIDEWEND_SHARED_BED } from './presets/tidewend';
import { WICKHAVEN } from './presets/wickhaven';
import { BRACKENMOOR } from './presets/brackenmoor';
import { COIL_UNDERCROFT } from './presets/coilUndercroft';
import { TOMB_WAKING } from './presets/tombWaking';
import { HUSH_NOOK } from './presets/hushNook';
// On-Ramp Restructure — starter combat field (first_field). Lightweight so the
// on-ramp doesn't stall on modest screens; was incorrectly picking `meadow`.
import { FIRST_FIELD } from './presets/firstField';
import { DROWNED_ROAD, LANTERNWOOD, TIDEWEND_OUTSKIRTS } from './presets/hollowCoilCorridor';
// On-Ramp Restructure S3 — cozy village arrival, warm hearth after the tomb.
import { VILLAGE_ARRIVAL } from './presets/villageArrival';
// Hollow Coil side branches — subterranean caverns and fen pasture.
import { MOONFALL_HOLLOW } from './presets/moonfallHollow';
import { LANTERNROOT_HOLLOW } from './presets/lanternrootHollow';
import { MOONFEN_PASTURE } from './presets/moonfenPasture';
import { FOREST_JOURNEY_PRESETS } from './presets/forestJourney';

/** A floating glow mote (firefly) — glow-soft sprite, additive, wandering. */
export interface FireflySpec {
  count: number;
  colors: number[];
  /** Per-mote glow sprite size range (px). */
  sizeMin: number;
  sizeMax: number;
  /** Wander drift radius (px). */
  driftPx: number;
  /** Alpha-waver + drift cycle range (ms) — async per mote. */
  periodMinMs: number;
  periodMaxMs: number;
  alphaMin: number;
  alphaMax: number;
}

/** A god-ray shaft — ray-ribbon sprite, screen blend, breathing. */
export interface GodRaySpec {
  count: number;
  color: number;
  widthMin: number;
  widthMax: number;
  height: number;
  /** Tilt range (degrees). */
  tiltDeg: number;
  periodMinMs: number;
  periodMaxMs: number;
  alphaMin: number;
  alphaMax: number;
}

/**
 * An atmosphere effect anchored within the initial camera scatter region.
 * Use this for ambient composition that should adapt to the opening view.
 */
export interface NormalizedEffectAnchor {
  atX: number;
  atY: number;
  x?: never;
  y?: never;
}

/**
 * An atmosphere effect anchored to authored level geometry in world pixels.
 * Use this for landmark lighting that must not move with the opening camera.
 */
export interface WorldEffectAnchor {
  x: number;
  y: number;
  atX?: never;
  atY?: never;
}

export type EffectAnchor = NormalizedEffectAnchor | WorldEffectAnchor;

/** A static radial glow blob (floor glow / canopy bloom). */
export type GlowBlobSpec = EffectAnchor & {
  color: number;
  size: number;
  alpha: number;
};

export interface AtmospherePreset {
  id: string;
  /** Ordered effect layers that make up the scene's ambiance. */
  layers: EffectLayer[];
}

export const ATMOSPHERE_PRESETS: Record<string, AtmospherePreset> = {
  cozy_forest: COZY_FOREST,
  subtle: SUBTLE,
  dungeon: DUNGEON,
  cave: CAVE,
  volcanic: VOLCANIC,
  arctic: ARCTIC,
  underwater: UNDERWATER,
  desert: DESERT,
  corrupted: CORRUPTED,
  swamp: SWAMP,
  fungal: FUNGAL,
  jungle: JUNGLE,
  meadow: MEADOW,
  celestial: CELESTIAL,
  autumn: AUTUMN,
  ashlands: ASHLANDS,
  tempest: TEMPEST,
  coast: COAST,
  radiant: RADIANT,
  moor: MOOR,
  savanna: SAVANNA,
  skylands: SKYLANDS,
  bamboo: BAMBOO,
  abyss: ABYSS,
  twilight: TWILIGHT,
  frostspire: FROSTSPIRE,
  crimson: CRIMSON,
  petrified: PETRIFIED,
  sakura: SAKURA,
  nexus: NEXUS,
  arena: ARENA,
  sunken_drift: SUNKEN_DRIFT,
  sky_whale: SKY_WHALE,
  void_serpent: VOID_SERPENT,
  fossil_graveyard: FOSSIL_GRAVEYARD,
  // The Hollow Coil cozy→spooky starter island — four bespoke biomes.
  pig_beach: PIG_BEACH,
  tidewend: TIDEWEND,
  wickhaven: WICKHAVEN,
  tidewend_port: { id: 'tidewend_port', layers: TIDEWEND_SHARED_BED.layers },
  wickhaven_port: { id: 'wickhaven_port', layers: WICKHAVEN.layers },
  tidewend_outskirts: TIDEWEND_OUTSKIRTS,
  drowned_road: DROWNED_ROAD,
  // Mallow's refuge beneath the road — a warm cellar interior, not the outdoor road bed.
  hush_nook: HUSH_NOOK,
  lanternwood: LANTERNWOOD,
  brackenmoor: BRACKENMOOR,
  coil_undercroft: COIL_UNDERCROFT,
  // On-Ramp Restructure — map 1 of 3 (candle-lit wet tomb, evolved from sunken_drift).
  tomb_waking: TOMB_WAKING,
  // On-Ramp Restructure S4 — starter combat field. Must be keyed by the biome
  // name authored in data/levels/first_field.json so resolveLayers picks it.
  first_field: FIRST_FIELD,
  // On-Ramp Restructure S3 — cozy village arrival, warm hearth after the tomb.
  village_arrival: VILLAGE_ARRIVAL,
  // Hollow Coil side branches — subterranean caverns and fen pasture.
  moonfall_hollow: MOONFALL_HOLLOW,
  lanternroot_hollow: LANTERNROOT_HOLLOW,
  moonfen_pasture: MOONFEN_PASTURE,
  ...FOREST_JOURNEY_PRESETS,
};

/**
 * Direct biome-name → preset lookup. This is the authoritative path the editor's
 * biome picker + `scene.biome` ride: a biome pinned by name selects its preset
 * regardless of the level id (no string-matching). Returns null for unknown /
 * empty names so callers can fall through to the id-based `presetForLevel`.
 */
export function presetByBiome(biome: string | null | undefined): AtmospherePreset | null {
  if (!biome) return null;
  return ATMOSPHERE_PRESETS[biome] ?? null;
}

/**
 * Per-destination portal tint — the portal's glow color reflects the MOOD of
 * the map it sends you to, so the portal into the Undercroft glows sickly-
 * green (not the default blue). This carries the cozy→spooky gradient into
 * the portal itself: the pad is a visual promise of what's ahead.
 *
 * Falls back to the classic teleporter blue (0x00aaff) for unknown maps.
 * Consulted by TeleporterEntity at construction time via `portalTintForLevel`.
 */
const PORTAL_TINTS: Record<string, number> = {
  deep_woods: 0x80966c,
  abandoned_camp: 0xb69b6b,
  old_grove: 0x91a477,
  // The Hollow Coil cozy→spooky gradient — portals tinted per destination mood.
  pig_beach: 0x8fd3f0, // bright warm-blue sky (cozy, sunny)
  tidewend: 0xffb454, // warm amber dusk (cozy, a little worried)
  brackenmoor: 0x607d78, // cold blue-green night (the turn, tense)
  coil_undercroft: 0x6fae4e, // sickly green dread (the dark heart)
  moonfall_hollow: 0x4ef0b8, // bioluminescent lunar cyan
  lanternroot_hollow: 0x67a879, // deep root-dark green
  moonfen_pasture: 0x76b8a8, // cold moonlit fen teal
  // Cross-biome leviathan showcases — portals tinted to their biome's signature.
  sunken_drift: 0x1fb6a6, // deep teal (drowned cathedral)
  sky_whale: 0xcdb4ff, // pale violet (sky leviathan)
  void_serpent: 0x7d2ae8, // corrupt violet (void serpent)
  fossil_graveyard: 0xd8cba8, // dusty bone (fossil graveyard)
};

/** The classic teleporter blue — the fallback when no per-biome tint exists. */
export const DEFAULT_PORTAL_TINT = 0x00aa_ff;

/**
 * Resolve a portal tint color for a destination level id. The tint reflects
 * the destination's atmosphere mood (cozy warm → cold → sickly green dread),
 * carrying the emotional gradient into the portal itself. Falls back to the
 * classic teleporter blue for unmapped levels.
 */
export function portalTintForLevel(levelId: string): number {
  const id = levelId.toLowerCase();
  return PORTAL_TINTS[id] ?? DEFAULT_PORTAL_TINT;
}

/**
 * The Hollow Coil cozy→spooky starter island — per-map atmosphere routes.
 *
 * The arc is an EMOTIONAL GRADIENT (bright beach → amber village → cold moor →
 * dark undercroft) carried by atmosphere. These EXACT-ID routes pin each Coil
 * map to a DISTINCT bespoke preset so the gradient isn't flattened: the old
 * keyword waterfall swallowed `tidewend` + `brackenmoor` (and `coil_undercroft`
 * via 'coil') into a single bucket — `tidewend`/`brackenmoor`→`cozy_forest`, so
 * the spooky climax rendered cozy — and `pig_beach` matched nothing (→ null).
 * Exact-id (not keyword) so the moor↔brackenmoor / coil↔dungeon id collisions
 * can't reorder us; consulted BEFORE the keyword waterfall below.
 *
 * The four bespoke presets (pigBeach.ts / tidewend.ts / brackenmoor.ts /
 * coilUndercroft.ts) are now live — see docs/design/biomes/ for blueprints.
 */
const COIL_ISLAND_PRESETS: Record<string, AtmospherePreset> = {
  pig_beach: ATMOSPHERE_PRESETS.pig_beach, //        ⇄ PIG_BEACH      — bright warm-blue sunny shore (brightest anchor)
  tidewend: ATMOSPHERE_PRESETS.tidewend, //          ⇄ TIDEWEND       — moonlit coastal harbor, warm inhabited streets
  tidewend_outskirts: ATMOSPHERE_PRESETS.tidewend_outskirts, // ⇄ moonlit lantern bed locked against the day clock
  drowned_road: ATMOSPHERE_PRESETS.drowned_road, //   ⇄ sparse cold moonlight over the flooded causeway
  lanternwood: ATMOSPHERE_PRESETS.lanternwood, //     ⇄ dark green night + the last four lamp pools
  brackenmoor: ATMOSPHERE_PRESETS.brackenmoor, //    ⇄ permanent cold blue-green night, low fog (the turn)
  coil_undercroft: ATMOSPHERE_PRESETS.coil_undercroft, // ⇄ COIL_UNDERCROFT — near-black sickly-green dread (dread anchor)
  moonfall_hollow: ATMOSPHERE_PRESETS.moonfall_hollow, // ⇄ subterranean lunar grotto & sunken ruins
  lanternroot_hollow: ATMOSPHERE_PRESETS.lanternroot_hollow, // ⇄ tangled root abyss beneath Lanternwood
  moonfen_pasture: ATMOSPHERE_PRESETS.moonfen_pasture, // ⇄ cold moonlit fen pasture
};

/**
 * Map a loaded level id → preset. Biome Nexus + the Hollow Coil island's
 * exact-id gradient routes match first, then specific biomes, then the broad
 * cozy-forest net, then ability grounds; everything else gets none (null).
 */
export function presetForLevel(levelId: string): AtmospherePreset | null {
  const id = levelId.toLowerCase();

  if (id === 'dark_forest') return FOREST_JOURNEY_PRESETS.forest_road;
  if (id === 'deep_woods' || id === 'abandoned_camp' || id === 'old_grove') return FOREST_JOURNEY_PRESETS[id];

  if (id === 'wickhaven' || id === 'wickhaven_port' || id === 'tidewend_port') return ATMOSPHERE_PRESETS[id];

  // Biome Nexus showcase hub — its own ethereal soft-light concourse.
  if (id.includes('nexus')) return ATMOSPHERE_PRESETS.nexus;

  // The Drowned Cathedral — exact-id so the sunken_drift preset (leviathans +
  // schooling motes) wins over the generic 'sunken'→underwater keyword route.
  if (id === 'sunken_drift' || id.includes('drowned_cathedral')) return ATMOSPHERE_PRESETS.sunken_drift;

  // On-Ramp tomb — exact-id so the bespoke candle preset wins over the generic
  // 'tomb'/'crypt'→dungeon keyword route below.
  if (id === 'tomb_waking') return ATMOSPHERE_PRESETS.tomb_waking;

  // On-Ramp village — exact-id so the warm-hearth preset wins over the broad
  // town/forest cozy net below.
  if (id === 'village_arrival') return ATMOSPHERE_PRESETS.village_arrival;

  // Cross-biome leviathan presets — exact-id routes so they win over keyword
  // routes (sky→skylands, void→corrupted, fossil→petrified).
  if (id === 'sky_whale') return ATMOSPHERE_PRESETS.sky_whale;
  if (id === 'void_serpent') return ATMOSPHERE_PRESETS.void_serpent;
  if (id === 'fossil_graveyard') return ATMOSPHERE_PRESETS.fossil_graveyard;

  // The Hollow Coil starter island — exact-id gradient routes (see
  // COIL_ISLAND_PRESETS). Wins over every keyword route below so the four maps
  // stay distinct and the cozy→spooky gradient lands.
  const coilPreset = COIL_ISLAND_PRESETS[id];
  if (coilPreset) return coilPreset;

  // Mode surfaces: /brawl (brawl-arena, the canonical social/stream hub) +
  // /arena (arena-multi-level, the PvP testbed) — the most-watched maps,
  // previously bare. Combat-clean dusk-colosseum bed. EXCLUDE the ability-test
  // grounds (e.g. 'ability-test-arena'), which keep the deliberately-bare
  // 'subtle' preset below so atmosphere never fights ability VFX QA.
  if ((id.includes('brawl') || id.includes('arena'))
      && !id.includes('ability') && !id.includes('grounds') && !id.includes('test')) {
    return ATMOSPHERE_PRESETS.arena;
  }

  // Biome-specific first (most specific wins).
  if (id.includes('volcano') || id.includes('lava') || id.includes('magma')) return ATMOSPHERE_PRESETS.volcanic;
  if (id.includes('frozen') || id.includes('ice') || id.includes('snow') || id.includes('arctic') || id.includes('glacier')) return ATMOSPHERE_PRESETS.arctic;
  if (id.includes('sunken') || id.includes('underwater') || id.includes('reef') || id.includes('abyss')) return ATMOSPHERE_PRESETS.underwater;
  // Fungal BEFORE cave — 'glowspore_grotto' contains 'grotto' (a cave keyword), but its distinct
  // fungal substrings win first so it gets the bioluminescent preset, not the crystal cave.
  if (id.includes('fungal') || id.includes('glowspore') || id.includes('spore') || id.includes('myco') || id.includes('mushroom') || id.includes('shroom')) return ATMOSPHERE_PRESETS.fungal;
  if (id.includes('crystal') || id.includes('cavern') || id.includes('cave') || id.includes('grotto')) return ATMOSPHERE_PRESETS.cave;
  if (id.includes('undercroft') || id.includes('temple') || id.includes('dungeon') || id.includes('crypt') || id.includes('ruin') || id.includes('coil')) return ATMOSPHERE_PRESETS.dungeon;
  if (id.includes('desert') || id.includes('dune') || id.includes('sand') || id.includes('oasis') || id.includes('mesa') || id.includes('scorch') || id.includes('badland')) return ATMOSPHERE_PRESETS.desert;
  if (id.includes('corrupt') || id.includes('blight') || id.includes('wither') || id.includes('void') || id.includes('decay') || id.includes('plague')) return ATMOSPHERE_PRESETS.corrupted;
  if (id.includes('swamp') || id.includes('bog') || id.includes('marsh') || id.includes('mire') || id.includes('fen') || id.includes('bayou') || id.includes('murk')) return ATMOSPHERE_PRESETS.swamp;
  if (id.includes('jungle') || id.includes('verdant') || id.includes('canopy') || id.includes('rainforest') || id.includes('tropic')) return ATMOSPHERE_PRESETS.jungle;
  if (id.includes('meadow') || id.includes('sunlit') || id.includes('grassland') || id.includes('prairie') || id.includes('pasture') || id.includes('glade')) return ATMOSPHERE_PRESETS.meadow;
  if (id.includes('aether') || id.includes('celestial') || id.includes('astral') || id.includes('firmament')) return ATMOSPHERE_PRESETS.celestial; // 'expanse' dropped — shared with hallowed_expanse (radiant)
  if (id.includes('autumn') || id.includes('amber') || id.includes('grove') || id.includes('harvest') || id.includes('maple')) return ATMOSPHERE_PRESETS.autumn;
  if (id.includes('smolder') || id.includes('ashland') || id.includes('cinder') || id.includes('ashen')) return ATMOSPHERE_PRESETS.ashlands;
  if (id.includes('tempest') || id.includes('storm') || id.includes('gale') || id.includes('squall') || id.includes('thunder')) return ATMOSPHERE_PRESETS.tempest;
  if (id.includes('coast') || id.includes('shore') || id.includes('sunkissed') || id.includes('seaside') || id.includes('lagoon')) return ATMOSPHERE_PRESETS.coast;
  if (id.includes('radiant') || id.includes('hallow') || id.includes('divine') || id.includes('sanctum') || id.includes('seraph')) return ATMOSPHERE_PRESETS.radiant;
  if (id.includes('windswept') || id.includes('heath') || id.includes('highland') || id.includes('crag')) return ATMOSPHERE_PRESETS.moor; // NOT 'moor' as a keyword — would substring-match brackenmoor (now exact-id routed above)
  if (id.includes('savanna') || id.includes('goldengrass') || id.includes('acacia') || id.includes('veldt')) return ATMOSPHERE_PRESETS.savanna;

  // Longer-tail biomes (batch 3). Keyword sets are mutually disjoint + collision-checked vs the
  // ~110 above. Notes: skylands drops 'reach' (it lives in crimson's 'bloodmoon_reach' map id);
  // abyss routes on sunless/stygian/… NOT 'abyss' (that keyword is owned by underwater above);
  // twilight + sakura both keep 'vale' OUT of routing (shared between duskfall_vale + sakura_vale).
  if (id.includes('skyreach') || id.includes('sky') || id.includes('isles') || id.includes('isle')) return ATMOSPHERE_PRESETS.skylands;
  if (id.includes('bamboo') || id.includes('whisper') || id.includes('culm') || id.includes('stalk') || id.includes('serene')) return ATMOSPHERE_PRESETS.bamboo;
  if (id.includes('sunless') || id.includes('stygian') || id.includes('fathom') || id.includes('gloom') || id.includes('chasm') || id.includes('deep')) return ATMOSPHERE_PRESETS.abyss;
  if (id.includes('dusk') || id.includes('duskfall') || id.includes('twilight') || id.includes('gloaming') || id.includes('eventide')) return ATMOSPHERE_PRESETS.twilight;
  if (id.includes('frostspire') || id.includes('frost') || id.includes('spire') || id.includes('peaks') || id.includes('peak')) return ATMOSPHERE_PRESETS.frostspire;
  if (id.includes('bloodmoon') || id.includes('crimson') || id.includes('scarlet') || id.includes('lunar') || id.includes('cursed')) return ATMOSPHERE_PRESETS.crimson;
  if (id.includes('petrified') || id.includes('stoneveil') || id.includes('stone') || id.includes('veil') || id.includes('barrens')) return ATMOSPHERE_PRESETS.petrified;
  if (id.includes('sakura') || id.includes('blossom') || id.includes('petal') || id.includes('cherry') || id.includes('hanami')) return ATMOSPHERE_PRESETS.sakura;

  // Broad cozy net (forest/town/social). The canonical Coil ids (pig_beach /
  // tidewend / brackenmoor / coil_undercroft) are exact-id routed above; this
  // net only catches generic forest/town/social maps + Coil-island sub-locations.
  if (id.includes('lobby') || id.includes('town') || id.includes('forest') || id.includes('hollow') || id.includes('island')) {
    return ATMOSPHERE_PRESETS.cozy_forest;
  }
  if (id.includes('grounds') || id.includes('ability')) {
    return ATMOSPHERE_PRESETS.subtle;
  }
  return null;
}
